import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import {
  Avatar,
  GridLayout,
  GridLayoutItem,
} from "@progress/kendo-react-layout";
import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { Switch } from "@progress/kendo-react-inputs";
import AlertBox from "../../components/common/AlertBox";
import { LoadingPanel } from "../../components/layout/Loading";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { MdDelete } from "react-icons/md";

import { checkAcessRights } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";
import {
  ActiveInactiveClientLogo,
  deleteClientLogo,
  getAllClientLogos,
} from "./services/clientLogo.services";
import { clearClientLogoID, setClientLogoID } from "./clientLogoSlice";
import ButtonCreate from "../../components/drawer/ButtonCreate";
import ButtonEdit from "../../components/drawer/ButtonEdit";
import { Tooltip } from "@mui/material";
import IconButton from "../../components/common/IconButton";
import LinkEdit from "../../components/drawer/LinkEdit";

const ClientLogo: React.FC = () => {
  const dispatch = useAppDispatch();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  useEffect(() => {
    dispatch(getAllClientLogos());
    return () => {
      dispatch(closeDialog());
    };
  }, []);

  return (
    <>
      <DeleteClientLogoDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Client List
          </Typography.h4>
          {isAddAccess && (
            <ButtonCreate label="Create Client" drawerName="clientLogoForm" />
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <ClientLogoGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const ClientLogoGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const ClientLogoList = useAppSelector(
    (state) => state.clientLogo.ClientLogoList
  );
  const loading = useAppSelector((state) => state.clientLogo.loading);

  useEffect(() => {
    dispatch(getAllClientLogos());
    return () => {
      dispatch(closeDialog());
    };
  }, []);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? ClientLogoList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteClientLogo"));
      dispatch(setClientLogoID(ID));
    };

    return (
      <td
        className={`action-td ${props.className}`}
        style={{
          ...props.style,
        }}
      >
        {isEditAccess && (
          <ButtonEdit
            ID={props.dataItem.ID}
            drawerName="clientLogoForm"
            tooltipTitle="Edit"
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            themeColor="error"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)
            }
            icon={MdDelete}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 2 || props.index === 3 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const MyActiveInActtiveCommandCell = (props: GridCellProps) => {
    const [IsActive, setIsActive] = useState<boolean>(
      props.dataItem.IsActive ?? false
    );

    const handleActiveInActive = () => {
      setIsActive(!IsActive);
      const data = {
        ID: props.dataItem.ID,
        IsActive: !IsActive,
      };
      dispatch(ActiveInactiveClientLogo(data)).then((response: any) => {
        // dispatch(getAllClientLogos());
        if (response.payload?.data?.Status === 200) {
          dispatch(getAllClientLogos());
        } else {
          dispatch(getAllClientLogos());
        }
      });
    };

    return (
      <td
        style={{ textAlign: "center", ...props.style }}
        className={props.className}
      >
        <Switch
          disabled={isEditAccess ? false : true}
          name="IsActive"
          checked={IsActive}
          onChange={handleActiveInActive}
          size="small"
        />
      </td>
    );
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      {ClientLogoList && ClientLogoList.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
          filterable={true}
          filter={filter}
          className="responsive-table"
          data={
            filter
              ? filterBy(ClientLogoList, filter).slice(
                page.skip,
                page.take + page.skip
              )
              : ClientLogoList.slice(page.skip, page.take + page.skip)
          }
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={ClientLogoList.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="ClientName"
            title="Client Name"
            width={250}
            cell={(props: any) => (
              <td
                className={`${isEditAccess ? "hoverableName" : "normalName"}`}
              >
                {isEditAccess ? (
                  <LinkEdit
                    ID={props.dataItem.ID}
                    drawerName="clientLogoForm"
                    label={props.dataItem?.ClientName}
                  />
                ) : (
                  <div className="normalName">
                    {props.dataItem?.ClientName}
                  </div>
                )}
              </td>
            )}
          />
          <Column
            field="Logo"
            title="Logo"
            width={110}
            filterable={false}
            cell={(props: any) => (
              <td style={{ textAlign: "center" }}>
                <Avatar
                  style={{
                    cursor: "pointer",
                    width: "100%",
                    height: "100%",
                    flexBasis: 45,
                    backgroundColor: "white",
                  }}
                  rounded={"medium"}
                  type={"image"}
                >
                  <Tooltip
                    placement="left"
                    title={
                      <img
                        style={{ padding: "5px", backgroundColor: "white", height: 150 }}
                        src={props.dataItem?.Logo}
                        alt="Client Logo"
                        width="100%"
                        onClick={() => window.open(props.dataItem?.Logo)}
                      />
                    }
                  >
                    {props.dataItem?.Logo ? (
                      <img
                        style={{ padding: "10px" }}
                        src={props.dataItem?.Logo}
                        alt="Client Logo"
                        onClick={() => window.open(props.dataItem?.Logo)}
                      />
                    ) : (
                      <>
                        <IconButton
                          style={{
                            fontSize: "50px",
                            background: "lightgray",
                            padding: "5px",
                            borderRadius: "4px",
                            color: "#163064",
                          }}
                        />
                      </>
                    )}
                  </Tooltip>
                </Avatar>
              </td>
            )}
          />
          <Column
            field="ID"
            title="Status"
            cell={MyActiveInActtiveCommandCell}
            width={"90px"}
            filterable={false}
            locked={true}
          />
          {(isEditAccess || isDeleteAccess) && (
            <Column
              field="ID"
              title="Actions"
              cell={MyEditCommandCell}
              width={"110px"}
              locked={true}
              filterable={false}
            />
          )}
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteClientLogoDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const ClientLogoID = useAppSelector((state) => state.clientLogo.ClientLogoID);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteClientLogo(ID));
      if (response.payload.Data.Status === 200) {
        dispatch(getAllClientLogos());
        dispatch(closeDialog());
        dispatch(clearClientLogoID());
      } else {
        dispatch(getAllClientLogos());
        dispatch(closeDialog());
        dispatch(clearClientLogoID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteClientLogo" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Client"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this client ?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(ClientLogoID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default ClientLogo;
